import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, RefreshCw } from 'lucide-react';
import { STATUS_LABEL, formatDateTime, formatDay, todayUk } from '@nu/shared';
import { api } from '../../api/client.js';
import { addDays } from '../bookings/shared.jsx';
import { problem } from '../bookings/util.js';

// Office: Stock > Stock used on jobs. What the engineers logged on each job
// (step 4 of the job card, or the office's corrections), by the job's day:
// each item's total across the days, then every job with its stock. "To
// order" is what isn't on a team order yet (it fills the team's next order).
// GET /api/job-stock/used.
const monday = (day) => addDays(day, -((new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7));
const ORDER_STATE = { ordered: 'Ordered', picked: 'Ready to collect', handed_over: 'Ready to collect', collected: 'Collected', cancelled: 'Order cancelled' };

export default function StockUsed() {
  const today = todayUk();
  const [from, setFrom] = useState(monday(today));
  const [to, setTo] = useState(today);
  const [teamId, setTeamId] = useState('');
  const [find, setFind] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api(`/job-stock/used?from=${from}&to=${to}${teamId ? `&teamId=${teamId}` : ''}`));
    } catch (e) {
      setError(problem(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [from, to, teamId]); // eslint-disable-line react-hooks/exhaustive-deps
  const range = (a, b) => { setFrom(a); setTo(b); };

  // Search narrows both lists: an item (description or SKU), a job, an address or a postcode.
  const q = find.trim().toLowerCase();
  const itemHit = (l) => !q || `${l.description} ${l.sku}`.toLowerCase().includes(q);
  const jobs = useMemo(() => (data?.jobs || []).filter((j) => !q || `${j.reference} ${j.address} ${j.postcode}`.toLowerCase().includes(q) || j.lines.some(itemHit)), [data, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const items = useMemo(() => {
    if (!q) return data?.items || [];
    const hitJob = (j) => `${j.reference} ${j.address} ${j.postcode}`.toLowerCase().includes(q);
    const byItem = new Map();
    for (const j of data?.jobs || []) {
      for (const l of j.lines) {
        if (!hitJob(j) && !itemHit(l)) continue;
        const i = byItem.get(l.itemId) || { itemId: l.itemId, sku: l.sku, description: l.description, qty: 0, jobs: 0, toOrder: 0 };
        i.qty += l.qty;
        i.jobs += 1;
        if ((!l.order || l.order.status === 'cancelled') && l.autoOrder) i.toOrder += l.qty;
        byItem.set(l.itemId, i);
      }
    }
    return [...byItem.values()].sort((a, b) => b.qty - a.qty || a.description.localeCompare(b.description));
  }, [data, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const totalQty = items.reduce((n, i) => n + i.qty, 0);

  const csv = () => {
    const head = ['Day', 'Team', 'Job', 'Address', 'Postcode', 'Job status', 'Item', 'SKU', 'Qty', 'Order', 'Logged by', 'Logged at'];
    const rows = jobs.flatMap((j) => j.lines.map((l) => [
      formatDay(j.day), j.teamName, j.reference, j.address, j.postcode, STATUS_LABEL[j.status] || j.status,
      l.description, l.sku, l.pack ? `${l.qty} ${l.pack.unit}` : l.qty, l.order ? `${l.order.ref} (${ORDER_STATE[l.order.status] || l.order.status})` : l.autoOrder ? (l.pack ? 'Counts towards next pack' : 'Added to next order') : 'Not added to orders',
      j.loggedBy, j.loggedAt ? formatDateTime(j.loggedAt) : '',
    ]));
    const text = [head, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([String.fromCharCode(0xfeff) + text], { type: 'text/csv' }) /* BOM: Excel reads it as UTF-8 */);
    a.download = `Stock used on jobs ${formatDay(from).split('/').reverse().join('-')}${from === to ? '' : ` to ${formatDay(to).split('/').reverse().join('-')}`}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
  };

  return (
    <div className="stack">
      <section className="card">
        <div className="page-head">
          <div>
            <h1>Stock used on jobs</h1>
            <p className="muted">What the engineers logged on each job when they completed it (and any office corrections), by the job’s day. Each item is labelled: “Added to next order” goes onto that team’s next stock order by itself (Add to orders ticked in the Job card stock organizer); once the order is placed it shows the order number instead. “Not added to orders” won’t be ordered by itself.</p>
          </div>
        </div>
        <div className="filters job-times-filters">
          <label className="fld"><span>From</span><input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} /></label>
          <label className="fld"><span>To</span><input type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} /></label>
          <label className="fld">
            <span>Team</span>
            <select value={teamId} onChange={(e) => setTeamId(e.target.value)}>
              <option value="">All teams</option>
              {(data?.teams || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </label>
          <label className="fld"><span>Search</span><input type="search" value={find} onChange={(e) => setFind(e.target.value)} placeholder="Item, SKU, job, postcode" /></label>
          <div className="job-times-quick">
            <button type="button" className="btn" onClick={() => range(today, today)}>Today</button>
            <button type="button" className="btn" onClick={() => range(addDays(today, -1), addDays(today, -1))}>Yesterday</button>
            <button type="button" className="btn" onClick={() => range(monday(today), today)}>This week</button>
            <button type="button" className="btn" onClick={() => range(addDays(monday(today), -7), addDays(monday(today), -1))}>Last week</button>
          </div>
        </div>
        <div className="survey-tools">
          <button type="button" className="btn primary with-icon" disabled={loading} onClick={load}><RefreshCw size={16} aria-hidden="true" className={loading ? 'spin' : ''} /> Refresh</button>
          <button type="button" className="btn primary with-icon" disabled={!jobs.length} onClick={csv}><Download size={16} aria-hidden="true" /> Download CSV</button>
        </div>
        {error && <p className="notice-line notice-bad">{error}</p>}
        {data && jobs.length > 0 && (
          <div className="job-times-sum">
            <div><span className="small muted">Jobs</span><strong>{jobs.length}</strong></div>
            <div><span className="small muted">Items used</span><strong>{totalQty}</strong><span className="small muted">{items.length} kinds</span></div>
            <div><span className="small muted">Added to next order</span><strong>{items.reduce((n, i) => n + i.toOrder, 0)}</strong><span className="small muted">not ordered yet</span></div>
          </div>
        )}
      </section>

      {!data && !error && <section className="card"><p className="muted">Loading…</p></section>}
      {data && !jobs.length && <section className="card"><p className="muted">No stock logged on jobs {from === to ? `on ${formatDay(from)}` : `from ${formatDay(from)} to ${formatDay(to)}`}{q ? ' matching the search' : ''}.</p></section>}

      {jobs.length > 0 && (
        <section className="card">
          <h2>By item</h2>
          <div className="table-scroll">
            <table className="table roster used-items">
              <thead><tr><th>Item</th><th>SKU</th><th className="num">Used</th><th className="num">On jobs</th><th className="num">Added to next order</th></tr></thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.itemId}>
                    <td><strong>{i.description}</strong></td>
                    <td className="small muted">{i.sku}</td>
                    <td className="num"><strong>{i.qty}</strong>{i.pack && <div className="cell-sub">{i.pack.unit}, packs of {i.pack.size}</div>}</td>
                    <td className="num">{i.jobs}</td>
                    <td className="num">{i.toOrder || '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {jobs.length > 0 && (
        <section className="card">
          <h2>By job</h2>
          <div className="table-scroll">
            <table className="table roster used-jobs">
              <colgroup><col className="uj-day" /><col className="uj-job" /><col className="uj-team" /><col /><col className="uj-by" /></colgroup>
              <thead><tr><th>Day</th><th>Job</th><th>Team</th><th>Stock used</th><th>Logged by</th></tr></thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.bookingId}>
                    <td>{formatDay(j.day)}</td>
                    <td>
                      <Link to={`/bookings/list?booking=${j.bookingId}`}><strong>{j.reference}</strong></Link>
                      <div className="cell-sub">{j.address}, {j.postcode}</div>
                      <div className="cell-sub">{STATUS_LABEL[j.status] || j.status}</div>
                    </td>
                    <td>{j.teamName || '–'}</td>
                    <td>
                      <ul className="used-lines">
                        {j.lines.filter((l) => !q || itemHit(l) || !j.lines.some(itemHit)).map((l) => (
                          <li key={l.itemId}>
                            <strong>{l.pack ? `${l.qty} ${l.pack.unit}` : `${l.qty} ×`}</strong> {l.description} <span className="small muted">{l.sku}</span>{' '}
                            {l.order && l.order.status !== 'cancelled'
                              ? <span className="used-tag done">{l.order.ref} · {ORDER_STATE[l.order.status] || l.order.status}</span>
                              : l.autoOrder ? <span className="used-tag next" title={l.pack ? `A pack goes on the next order for each ${l.pack.size} ${l.pack.unit} used` : undefined}>{l.pack ? 'Counts towards next pack' : 'Added to next order'}</span> : <span className="used-tag off" title="Add to orders is off for this item (Job card stock organizer), or Nano Fibre doesn’t let us order it">Not added to orders</span>}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="small">{j.loggedBy}<div className="cell-sub">{j.loggedAt ? formatDateTime(j.loggedAt) : ''}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
